// /opt/ptg01-v1/front-extranet/lib/useGlobalSearch.ts
// Sprint 2026-05-17 (Sprint 3b) — Hook recherche globale ⌘K.
// Debounce 200ms + React Query (cache résultats par query, 30s staleTime).
'use client';

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchExtranetJson } from './fetchExtranet';

export type SearchResultKind = 'MISSION' | 'CRA' | 'EXPENSE' | 'ACTIVITY';

export interface SearchResult {
    kind: SearchResultKind;
    id: string;
    title: string;
    subtitle: string;
    href: string;
}

export interface GlobalSearchResponse {
    query: string;
    missions: SearchResult[];
    cras: SearchResult[];
    expenses: SearchResult[];
    activity: SearchResult[];
    totalCount: number;
}

/** Debounce générique sans dépendance externe — préfère cette implé minimaliste. */
function useDebounced<T>(value: T, delay = 200): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

export function useGlobalSearch(query: string) {
    const debounced = useDebounced(query.trim(), 200);
    const enabled = debounced.length >= 2;

    return useQuery({
        queryKey: ['me', 'search', debounced] as const,
        queryFn: async () => {
            const res = await fetchExtranetJson<GlobalSearchResponse>(`/api/me/search?q=${encodeURIComponent(debounced)}`);
            if (!res.success) throw new Error(res.message || 'Erreur recherche');
            return res.data as GlobalSearchResponse;
        },
        enabled,
        staleTime: 30_000,   // garde les résultats au chaud 30s — éviter les refetch si user navigue palette
    });
}
